import { useEffect, useRef } from "react";

const IGNORE_SELECTOR = [
  "a",
  "button",
  "input",
  "textarea",
  "select",
  "label",
  "[contenteditable='true']",
  ".du-button",
  ".google-map-shell",
  ".map-detail-modal",
  ".event-rail",
  ".event-rail *",
].join(", ");

function isMobileDragViewport() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(max-width: 760px)")?.matches === true;
}

export default function DragScrollController() {
  const dragRef = useRef({
    active: false,
    pointerId: null,
    lastY: 0,
    lastTs: 0,
    velocity: 0,
    inertiaRaf: 0,
  });

  useEffect(() => {
    const stopInertia = () => {
      if (dragRef.current.inertiaRaf) {
        window.cancelAnimationFrame(dragRef.current.inertiaRaf);
        dragRef.current.inertiaRaf = 0;
      }
    };

    const runInertia = () => {
      stopInertia();
      let previous = performance.now();

      const tick = (now) => {
        const state = dragRef.current;
        const dt = Math.max(8, now - previous);
        previous = now;

        const delta = state.velocity * dt;
        window.scrollBy(0, delta);

        const decay = Math.pow(0.92, dt / 16.67);
        state.velocity *= decay;

        if (Math.abs(state.velocity) < 0.02) {
          state.inertiaRaf = 0;
          return;
        }
        state.inertiaRaf = window.requestAnimationFrame(tick);
      };

      dragRef.current.inertiaRaf = window.requestAnimationFrame(tick);
    };

    const onPointerDown = (event) => {
      if (!isMobileDragViewport()) return;
      if (event.button !== 0 || event.pointerType !== "mouse") return;
      if (event.target?.closest?.(IGNORE_SELECTOR)) return;

      stopInertia();
      dragRef.current.active = true;
      dragRef.current.pointerId = event.pointerId;
      dragRef.current.lastY = event.clientY;
      dragRef.current.lastTs = performance.now();
      dragRef.current.velocity = 0;
      document.body.classList.add("drag-scroll-active");
    };

    const onPointerMove = (event) => {
      const state = dragRef.current;
      if (!state.active) return;
      if (state.pointerId != null && event.pointerId !== state.pointerId) return;

      const now = performance.now();
      const dy = event.clientY - state.lastY;
      const dt = Math.max(8, now - state.lastTs);
      const scrollDelta = -dy * 1.28;

      window.scrollBy(0, scrollDelta);
      const instantVelocity = scrollDelta / dt;
      state.velocity = state.velocity * 0.76 + instantVelocity * 0.24;
      state.lastY = event.clientY;
      state.lastTs = now;
      event.preventDefault();
    };

    const endDrag = (event) => {
      const state = dragRef.current;
      if (!state.active) return;
      if (state.pointerId != null && event.pointerId !== state.pointerId) return;

      state.active = false;
      state.pointerId = null;
      document.body.classList.remove("drag-scroll-active");
      if (Math.abs(state.velocity) >= 0.02) runInertia();
    };

    window.addEventListener("pointerdown", onPointerDown, { passive: true });
    window.addEventListener("pointermove", onPointerMove, { passive: false });
    window.addEventListener("pointerup", endDrag, { passive: true });
    window.addEventListener("pointercancel", endDrag, { passive: true });

    return () => {
      stopInertia();
      document.body.classList.remove("drag-scroll-active");
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
    };
  }, []);

  return null;
}
